import { Check, Close, DeleteOutline, Edit, Replay } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import {
  FeedbackDetail,
  FeedbackHeader,
  FeedbackStatusChip,
} from "components/Feedback";
import { MainLayoutContainer } from "components/Layout";
import {
  useGetFeedbackByIdLazyQuery,
  useUpdateFeedbackMutation,
  useUpdateFeedbackSkillMutation,
} from "graphql/__generated__/hooks";
import { useBadgeChecker } from "hooks/useBadgeChecker";
import { useAuthUser } from "hooks/useAuthUser";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { FC, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { sendAlertViaEmail } from "reduxStore/alert/thunks";
import { ALERT_TYPES } from "helpers/constants";
import { AdminRoutes } from "config/routes";
import { useAppDispatch } from "hooks/useAppDispatch";
import { ROLES } from "types/user";
import { useRequestFeedbackUtils } from "../Commom/useRequestFeedbackUtils";
import {
  FEEDBACK_ACCESS_MESSAGE,
  FEEDBACK_ACCESS_TITLE,
  getFeedbackStatus,
} from "../../../utils/feedback";
import { VETCOIN_REWARDS, useVetcoin } from "hooks/useVetcoin";
import { recordEducationRecordRead } from "services/educationRecordAccessLog";
import { clearFeedbackReturnPath } from "services/accountSession";

export const FeedbackPortalDetail: FC = () => {
  const [displayApproveModal, setDisplayApproveModal] =
    useState<boolean>(false);
  const [displayDeclineModal, setDisplayDeclineModal] =
    useState<boolean>(false);
  const { feedbackId, organizationId } = useParams();
  const [getFeedback, { data, loading, error, called }] =
    useGetFeedbackByIdLazyQuery();
  const [updateFeedback] = useUpdateFeedbackMutation();
  const [updateFeedbackSkill] = useUpdateFeedbackSkillMutation();
  const [rate, setRate] = useState<number>(0);
  const { role, user } = useAuthUser();
  const isAdmin = useMemo(() => {
    return (
      role === ROLES.ADMIN || role === ROLES.MANAGER || role === ROLES.DIRECTOR
    );
  }, [role]);
  const dispatchRedux = useAppDispatch();
  const [showRetryDialog, setShowRetryDialog] = useState<boolean>(false);
  const [showHideDialog, setShowHideDialog] = useState<boolean>(false);
  const { handleRetryFeedbackRequest } = useRequestFeedbackUtils();
  const navigate = useNavigate();
  const { checkAndAwardBadge } = useBadgeChecker();
  const { awardVetcoins } = useVetcoin(user?.user_id);

  const fetchData = () =>
    getFeedback({
      variables: { feedbackId },
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    if (feedbackId) {
      fetchData();
      recordEducationRecordRead({
        resourceType: "feedback",
        resourceId: feedbackId,
      });
    }
  }, [feedbackId]);

  useEffect(() => {
    if (data?.feedback_by_pk) {
      clearFeedbackReturnPath();
    }
  }, [data]);

  const feedback = useMemo(() => {
    if (data?.feedback_by_pk?.feedback_skills?.[0]?.coach_rating) {
      setRate(data?.feedback_by_pk.feedback_skills[0].coach_rating || 0);
    }
    return data?.feedback_by_pk as FeedbackType | null;
  }, [data]);

  const customLabels = useMemo(() => {
    const list =
      data?.feedback_by_pk?.organization?.custom_labels?.map((ocl: any) =>
        ocl?.custom_label ? ocl.custom_label : null,
      ) || [];
    return (list.filter(Boolean) as any[]) || [];
  }, [data]);

  const status = useMemo(() => {
    if (!feedback) return null;
    return getFeedbackStatus(feedback as FeedbackType) as FeedbackStatus;
  }, [feedback]);

  const breadcrumb = useMemo(() => {
    if (isAdmin) {
      return organizationId
        ? BREADCRUMB_PAGE.FEEDBACK_ORGANIZATION
        : BREADCRUMB_PAGE.FEEDBACK;
    } else {
      return BREADCRUMB_PAGE.FEEDBACK_PORTAL;
    }
  }, [isAdmin, organizationId]);

  if (!feedbackId) {
    return (
      <MainLayoutContainer title="Feedback" breadcrumb={breadcrumb} size="small">
        <Alert severity="info">
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {FEEDBACK_ACCESS_TITLE}
          </Typography>
          <Typography variant="body2">{FEEDBACK_ACCESS_MESSAGE}</Typography>
        </Alert>
      </MainLayoutContainer>
    );
  }

  if (!called || loading) {
    return (
      <MainLayoutContainer title="Feedback" breadcrumb={breadcrumb} size="small">
        <Box>loading...</Box>
      </MainLayoutContainer>
    );
  }

  if (error && !feedback) {
    return (
      <MainLayoutContainer title="Feedback" breadcrumb={breadcrumb} size="small">
        <Alert severity="error">
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Couldn't load this feedback
          </Typography>
          <Typography variant="body2">
            {error.message || "The request failed. Try refreshing the page."}
          </Typography>
        </Alert>
      </MainLayoutContainer>
    );
  }

  if (!feedback) {
    return (
      <MainLayoutContainer title="Feedback" breadcrumb={breadcrumb} size="small">
        <Alert severity="info">
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {FEEDBACK_ACCESS_TITLE}
          </Typography>
          <Typography variant="body2">{FEEDBACK_ACCESS_MESSAGE}</Typography>
        </Alert>
      </MainLayoutContainer>
    );
  }

  const handleRetry = async (feedback: FeedbackType) => {
    setShowRetryDialog(false);
    await handleRetryFeedbackRequest(feedback);
  };

  const handleHideFeedback = async () => {
    if (!feedbackId) return;
    await updateFeedback({
      variables: {
        id: feedbackId,
        feedback: {
          status: FeedbackStatus.HIDDEN,
        },
      },
    });
    if (organizationId) {
      navigate(`/organization/${organizationId}/feedbacks`);
    } else if (isAdmin) {
      navigate(AdminRoutes.FeedbackList);
    } else {
      navigate("/feedback-portal");
    }
  };

  const handleApproveFeedback = async () => {
    if (feedback?.feedback_skills?.[0]?.feedback_skill_id && rate > 0) {
      await updateFeedbackSkill({
        variables: {
          id: feedback.feedback_skills[0].feedback_skill_id,
          feedback_skill: {
            coach_rating: rate,
          },
        },
      });
    }
    await updateFeedback({
      variables: {
        id: feedbackId,
        feedback: {
          status: FeedbackStatus.DONE,
          reviewed_at: new Date().toISOString(),
        },
      },
    });

    try {
      await awardVetcoins({
        userId: user?.user_id || "",
        amount: VETCOIN_REWARDS.FEEDBACK_EVALUATION_COMPLETED,
        actionType: "FEEDBACK_EVALUATION_COMPLETED",
        sourceFeedbackId: feedbackId,
        metadata: { flow: "coach_portal_save_and_approve" },
      });
    } catch (error) {
      console.error("Error awarding vetcoins for feedback approval:", error);
    }

    // Check for badge eligibility after approving feedback
    await checkAndAwardBadge();

    dispatchRedux(
      sendAlertViaEmail({
        feedbackId: feedback.feedback_id || "",
        coachName: feedback.coach.name || "",
        coachEmail: feedback.coach.email || "",
        studentName: feedback.student?.name || "",
        studentId: feedback.student?.user_id || "",
        studentEmail: feedback.student?.email || "",
        type: ALERT_TYPES.FEEDBACK_STATUS_UPDATE,
        status: FeedbackStatus.DONE,
      }),
    );
    fetchData();
    navigate(AdminRoutes.FeedbackSubmissionConfirmation, {
      state: {
        title: "Coach Feedback Successfully Submitted",
        vetcoinsAwarded: VETCOIN_REWARDS.FEEDBACK_EVALUATION_COMPLETED,
      },
    });
  };

  const handleDeclineFeedback = () => {
    updateFeedback({
      variables: {
        id: feedbackId,
        feedback: {
          status: FeedbackStatus.DECLINED,
          reviewed_at: new Date().toISOString(),
        },
      },
    }).then(() => {
      dispatchRedux(
        sendAlertViaEmail({
          feedbackId: feedback.feedback_id || "",
          coachName: feedback.coach.name || "",
          coachEmail: feedback.coach.email || "",
          studentName: feedback.student?.name || "",
          studentId: feedback.student?.user_id || "",
          studentEmail: feedback.student?.email || "",
          type: ALERT_TYPES.FEEDBACK_STATUS_UPDATE,
          status: FeedbackStatus.DECLINED,
        }),
      );
      fetchData();
    });
  };

  return (
    <MainLayoutContainer
      title={`Feedback from ${feedback.student?.name || "learner"}`}
      size="small"
      breadcrumb={breadcrumb}
      buttonActions={
        <Stack direction="row" spacing={2}>
          {(getFeedbackStatus(feedback) === FeedbackStatus.EXPIRED ||
            getFeedbackStatus(feedback) === FeedbackStatus.DECLINED) && (
            <Box pl={2}>
              {user?.role === ROLES.STUDENT && (
                <Tooltip title={"Retry Feedback Request"} arrow>
                  <IconButton
                    sx={{
                      borderRadius: "50%",
                    }}
                    color={"error"}
                    onClick={() => {
                      setShowRetryDialog(true);
                    }}
                  >
                    <Replay />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={"Delete Feedback Request"} arrow>
                <IconButton
                  sx={{
                    borderRadius: "50%",
                  }}
                  color={"error"}
                  onClick={() => {
                    setShowHideDialog(true);
                  }}
                >
                  <DeleteOutline />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {getFeedbackStatus(feedback) === FeedbackStatus.DRAFT &&
            user?.role === ROLES.STUDENT && (
              <Box pl={2}>
                <Button
                  sx={{
                    mt: 0.5,
                  }}
                  variant="outlined"
                  size="small"
                  color="primary"
                  component={Link}
                  to={`/request-feedback/${feedbackId}`}
                  startIcon={<Edit />}
                >
                  Continue Feedback
                </Button>
              </Box>
            )}

          <Box pl={2}>
            <Avatar src={feedback.student?.picture_url} />
          </Box>
        </Stack>
      }
    >
      <Box>
        <Box my={2} ml={1}>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <FeedbackStatusChip feedback={feedback} />
            {status === FeedbackStatus.MISSING_INFO &&
              role === ROLES.STUDENT && (
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  component={Link}
                  to={`/request-feedback/${feedbackId}`}
                  startIcon={<Edit />}
                >
                  Complete Missing Info
                </Button>
              )}
          </Stack>
        </Box>

        <Box mb={2}>
          <FeedbackHeader feedback={feedback} role={role} />
        </Box>

        <FeedbackDetail
          feedback={feedback}
          refetch={() => fetchData()}
          rate={rate}
          onRateChange={async (value) => {
            if (
              role === ROLES.COACH &&
              feedback.status === FeedbackStatus.PENDING &&
              feedback?.feedback_skills?.[0]?.feedback_skill_id
            ) {
              setRate(value);
              // Update the coach_rating in feedback_skill
              await updateFeedbackSkill({
                variables: {
                  id: feedback.feedback_skills[0].feedback_skill_id,
                  feedback_skill: {
                    coach_rating: value,
                  },
                },
              });
            }
          }}
          customLabels={customLabels}
        />
        <Box my={2}>
          <Divider />
        </Box>

        {role === "coach" &&
          feedback?.status === FeedbackStatus.PENDING &&
          feedback?.feedback_skills?.[0]?.coach_rating &&
          feedback?.feedback_skills?.[0]?.coach_comments && (
            <>
              <Box
                sx={{
                  my: 2,
                  gap: 2,
                  display: { xs: "block", md: "flex" },
                  justifyContent: { xs: "inherit", md: "space-between" },
                  alignItems: { xs: "inherit", md: "center" },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ display: { xs: "none", md: "inherit" } }}
                >
                  Please approve or decline this feedback.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Close />}
                    sx={{ width: { xs: "100%", md: "inherit" } }}
                    onClick={() => {
                      setDisplayDeclineModal(true);
                    }}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Check />}
                    sx={{ width: { xs: "100%", md: "inherit" } }}
                    onClick={() => {
                      setDisplayApproveModal(true);
                    }}
                  >
                    Save & Approve
                  </Button>
                </Stack>
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

        <ConfirmActionModal
          show={displayApproveModal}
          onConfirm={() => {
            handleApproveFeedback();
          }}
          onDismiss={() => {
            setDisplayApproveModal(false);
          }}
          title={"Approve Feedback"}
          message={"Are you sure you want to approve this feedback?"}
        />
        <ConfirmActionModal
          show={displayDeclineModal}
          onConfirm={() => {
            handleDeclineFeedback();
          }}
          onDismiss={() => {
            setDisplayDeclineModal(false);
          }}
          title={"Decline Feedback"}
          message={"Are you sure you want to decline this feedback?"}
        />
      </Box>
      {showRetryDialog && (
        <ConfirmActionModal
          title={"Retry Feedback Request"}
          message={"Do you want to retry this Feedback request?"}
          show={showRetryDialog}
          onConfirm={() => handleRetry(feedback)}
          onDecline={() => setShowRetryDialog(false)}
          onDismiss={() => {}}
          confirmButtonText="I Accept"
          declineButtonText="Decline"
        />
      )}
      {showHideDialog && (
        <ConfirmActionModal
          title={"Delete Feedback Request"}
          message={"Are you sure you want to delete this Feedback?"}
          show={showHideDialog}
          onConfirm={handleHideFeedback}
          onDismiss={() => {
            setShowHideDialog(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};
